import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { loginTotp } from '../api';
import { errorMessage } from '../lib/api';
import { Spinner } from '../components/Spinner';
import { ArrowLeft, RotateCw } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { followSessionSlot } from '../lib/accountSlot';

/**
 * Second factor of login, on its own route: the history navigation from
 * /login makes password managers (Bitwarden) re-scan the page so they offer
 * the TOTP code. Expects { mfaToken, from } in location.state; a direct
 * visit or refresh loses that state and bounces back to /login.
 */
export function LoginMfaPage() {
  useDocumentTitle('Two-factor authentication');
  const { setSession, isAuthenticated, initializing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const state = location.state as { mfaToken?: string; from?: string } | null;
  const mfaToken = state?.mfaToken;
  const from = state?.from ?? '/';

  if (!initializing && isAuthenticated) {
    return <Navigate to={from} replace />;
  }
  if (!mfaToken) {
    return <Navigate to="/login" replace />;
  }

  const onVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await loginTotp({ mfaToken, code: code.trim() });
      setSession(res);
      if (followSessionSlot(res, from)) return;
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err, 'Invalid authentication code'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-8 flex flex-col items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-ink text-white">
            <RotateCw className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              aiai·loop
            </h1>
            <p className="mt-1 text-sm text-ink-muted">
              Enter the 6-digit code from your authenticator
            </p>
          </div>
        </div>

        <form onSubmit={onVerify} className="card space-y-4 p-6">
          <div>
            <label className="label" htmlFor="mfaCode">
              Two-factor code
            </label>
            {/* id/name "mfaCode" mirror n8n's MFA form, which Bitwarden's
                TOTP field detection recognizes. No maxLength: this field
                also accepts 11-char recovery codes (xxxxx-xxxxx). */}
            <input
              id="mfaCode"
              name="mfaCode"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="One-time authentication code"
              enterKeyHint="done"
              className="input text-center font-mono text-lg tracking-[0.4em]"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. 123456"
              required
              autoFocus
            />
          </div>

          {error && (
            <div className="rounded-[5px] border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting && <Spinner />}
            {submitting ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            className="btn-ghost w-full text-xs"
            onClick={() => navigate('/login', { replace: true })}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back
          </button>
          {/* Must stay BELOW the buttons: Bitwarden reads the input's next
              siblings as "label-right" and hard-rejects the field as a TOTP
              target if it sees the words "recovery" or "backup" there
              (RecoveryCodeFieldNames check runs before the autocomplete
              check). Sibling scanning stops at a <button>. */}
          <p className="text-center text-xs text-ink-faint">
            Or enter a recovery code if you don't have your device.
          </p>
        </form>
      </div>
    </div>
  );
}
